import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { useState } from "react";

import { Config } from "@/components/crud/crud-config";
import { DynamicBadge } from "@/components/dynamic-badge";
import TableActions from "@/components/table-actions";
import { getEventStatusColors } from "@/components/table-utils/get-status-color";
import { PiCalendarPlusDuotone, PiList } from "react-icons/pi";
import useQueryKeys from "@/config/query-keys";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import {
    EventFormDto,
    eventFormSchema,
    EventResponseDto,
} from "@/Dtos/event-dto";
import AvatarCard from "@/ui/avatar-card";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Button, Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import PageHeader from "../shared/page-header";
import EventForm from "./event-calendar/event-form";
import ExportButton from "@/app/shared/export-button";
import ModalButton from "@/app/shared/modal-button";
import routes from "@/routers/routes";
import { metaObject } from "@/config/site.config";
import { eventData } from "@/data/event-data";
import { Filters } from "@/components/crud/filters";
import TablePro from "@/components/table-pro";
import TableFooterPro from "@/components/table-pro/footer";
import TablePaginationPro from "@/components/table-pro/pagination";
import { useTanStackTablePro } from "@/components/table-pro/custom/use-TanStack-Table-Pro";
import CalendarPage from "./calendar";
import DollarIcon from "@/components/icons/dollar";
import { navigate } from "./event-calendar/constants";
import { useNavigate } from "react-router-dom";

export const metadata = {
    ...metaObject("Event Calendar"),
};

const pageHeader = {
    title: "Events",
    breadcrumb: [
        {
            href: routes.dashboards.index,
            name: "Home",
        },
        {
            href: routes.eventCalendar,
            name: "Events",
        },
    ],
};

const config: Config<EventResponseDto, EventFormDto> = {
    endPoint: EndPoints.events.crud,
    queryKey: useQueryKeys.events,
    columns: [],
    validationSchema: eventFormSchema,
    permissionsCreate: PermissionsLists.Events.Create,
    formOptions: {
        customSize: "1200px",
        sendAsFormData: true,
        formClassName:
            "grid grid-cols-1 gap-5 @container md:grid-cols-2 lg:grid-cols-3 [&_label]:font-medium",
    },

    fields: [
        {
            name: "name",
            label: "Title",
            type: "text",
            hasFilter: true,
            filter: {
                operation: Operation.like,
            },
        },
        {
            name: "host",
            label: "Host",
            type: "text",
            hasFilter: true,
            // className: "w-auto",
            referEndPoint: EndPoints.entity.referTo,
            filter: {
                operation: Operation.eq,
                // showOperations: true,
            },
        },
        {
            name: "venue",
            label: "Venue",
            type: "text",
            hasFilter: true,
            referEndPoint: EndPoints.role.referTo,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "capacity",
            label: "Participants",
            type: "number",
            hasFilter: false,
        },
        {
            name: "seats",
            label: "Booked Seats",
            type: "number",
            hasFilter: false,
        },
        {
            name: "status",
            label: "Status",
            type: "select",
            options: [
                {
                    label: "Male",
                    value: "male" as string,
                },
                {
                    label: "Female",
                    value: "female" as string,
                },
            ],
            hasFilter: true,
            filter: {
                operation: Operation.eq,
            },
            props: {
                // getOptionLabel: (option) => option.label,
                // getOptionValue: (option) => option.value,
                optionClassName: "capitalize",
            },
        },
        {
            name: "picture",
            label: "Picture",
            type: "file",
        },
        {
            name: "password",
            label: "Password",
            type: "password",
            createFormOnly: true,
        },
        {
            name: "confirmPassword",
            label: "Confirm Password",
            type: "password",
            createFormOnly: true,
        },

        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            // className: "w-auto",
            filter: {
                operation: Operation.gte,
                // showOperations: true,
            },
        },
    ],
    titles: {
        pageTitle: "Scheduled Events",
    },
    filterProps: {
        useDrawer: true,
        // ignoreSorting: true,
        sortingClassName: "w-auto",
        // sortingMinWidth: "10px",
        directionClassName: "w-auto",
        // directionMinWidth: "10px",
    },
};

const columnHelper = createColumnHelper<EventResponseDto>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),

    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Title",
        enableSorting: true,
        cell: ({ row }) => (
            <AvatarCard
                src={
                    (row.original.image
                        ? row.original.image
                        : `${import.meta.env.VITE_APP_ImagePath}${row.original.image}`) ??
                    ""
                }
                name={row.original?.name ?? ""}
                description={row.original.host}
            />
        ),
    }),
    columnHelper.accessor("venue", {
        id: "venue",
        size: 150,
        header: "Venue",
        cell: ({ row }) => row.original.venue?.name ?? "-",
    }),
    columnHelper.accessor("capacity", {
        id: "capacity",
        size: 150,
        header: "Participants",
        cell: ({ row }) => row.original.capacity,
    }),
    columnHelper.accessor("seats", {
        id: "seats",
        size: 140,
        header: "Remaining Seats",
        cell: ({ row }) => row.original.seats,
    }),
    columnHelper.accessor("status", {
        id: "status",
        size: 140,
        header: "Status",
        cell: ({ row: { original } }) => (
            <DynamicBadge
                status={original.status}
                classNameBadge={getEventStatusColors(original.status)[1]}
                classNameText={getEventStatusColors(original.status)[0]}
            />
        ),
    }),
    columnHelper.accessor("startDate", {
        id: "date",
        size: 160,
        header: "Date",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.startDate ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();
            const navigate = useNavigate();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });

                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "Payments",
                            color: "green",
                            isLoading: isLoading,
                            icon: <DollarIcon className="size-4" />,
                            onClick: () => navigate(routes.events.payments(row.original.id!)),
                        }
                    ]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    const [showCalendar, setShowCalendar] = useState(false);

    const { table, isPending, data } = useTanStackTablePro<EventResponseDto>({
        queryKey: config.queryKey,
        endPoint: config.endPoint,
        columnConfig: config.columns,
        options: config.columnsOptions,
    });

    return (
        <>
            <PageHeader title="Events" breadcrumb={pageHeader.breadcrumb}>
                <div className="mt-4 flex items-center gap-3 @lg:mt-0">
                    <Button variant="outline"
                        onClick={() => setShowCalendar(!showCalendar)}
                        className="w-full @lg:w-auto">
                        {showCalendar && <>
                            <PiList className="me-1.5 h-[17px] w-[17px]" />
                            List View
                        </>}
                        {!showCalendar && <>
                            <PiCalendarPlusDuotone className="me-1.5 h-[17px] w-[17px]" />
                            Calendar View
                        </>}
                    </Button>
                    {false && <ExportButton
                        data={eventData}
                        fileName="event_data"
                        header="ID,Title,Description,Location,Start,end"
                    />}
                    <ModalButton
                        label="Create Event"
                        view={<EventForm />}
                        customSize="900px"
                        className="mt-0 w-full @lg:w-auto"
                    />
                </div>
            </PageHeader>

            {!showCalendar && <Box>
                <Filters config={config} table={table} />

                <TablePro
                    table={table}
                    variant="modern"
                    isLoading={isPending}
                    classNames={{
                        container: "border border-muted rounded-md",
                        rowClassName: "last:border-0",
                    }}
                />
                <TableFooterPro table={table} />
                <TablePaginationPro table={table} className="py-4" />
            </Box>}

            {showCalendar && <CalendarPage data={data!} />}
        </>
    );
}
